import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Helmet } from 'react-helmet';
import { graphql, navigate } from 'gatsby';
import { filter, kebabCase, toLower, orderBy } from 'lodash-es';
import { Button } from 'chaoskit/src/components';
import { config } from 'chaoskit/src/helpers/config';
import { unique } from 'chaoskit/src/helpers/utility';

import PressLayout from '../../layouts/PressLayout';
import MediaItem from '../../partials/press/MediaItem';

const List = ({
  data: {
    list: { edges: mediaEdges },
    categories: { edges: categoryEdges },
  },
  location,
}) => {
  const allCategory = {
    name: 'All',
    slug: 'all',
  };

  const [activeCategory, setActiveCategory] = useState(allCategory);
  const [results, setResults] = useState(mediaEdges);

  const getCategories = () => {
    let categoriesGroup = [];

    // Run through each category edge and flatten data
    categoryEdges.forEach(
      ({
        node: {
          frontmatter: { categories: mediaCategories },
        },
      }) => {
        mediaCategories.forEach(category => {
          categoriesGroup.push({
            name: category,
            slug: kebabCase(toLower(category)),
          });
        });
      }
    );

    // Get unique categories
    categoriesGroup = unique(categoriesGroup, 'name');

    return orderBy(categoriesGroup, ['name'], 'desc');
  };

  const handleMediaCategoryClick = category => {
    setActiveCategory(category);

    if (category.slug === 'all') {
      setResults(mediaEdges);
    } else {
      // Always filter on original results
      const filtered = filter(mediaEdges, ({ node }) =>
        node.frontmatter.categories.includes(category.name)
      );

      setResults(filtered);
    }

    // Add hash to URL
    navigate(`/press/media/#${kebabCase(toLower(category.slug))}`);
  };

  // Determine if we need to filer our results onload
  useEffect(() => {
    if (location.hash) {
      const slug = location.hash.substring(1);

      // Attempt to find matching category
      const matchedCategory = getCategories().find(
        category => category.slug === slug
      );

      if (matchedCategory) {
        handleMediaCategoryClick(matchedCategory);
      }
    }
  }, []);

  const getCategoryNavClasses = category =>
    cx('press__media-nav-listLink', {
      [config.classes.active]: activeCategory.slug === category.slug,
    });

  return (
    <PressLayout>
      <Helmet
        title={`Media Coverage for Gremlin (${activeCategory.name})`}
        meta={[
          {
            name: 'description',
            content: 'News, company info, and media resources',
          },
        ]}
      />
      <div className="container">
        <div className="press__media-wrapper">
          <nav className="press__media-nav u-contrast">
            <ul className="u-list press__media-nav-list">
              <li>
                <Button
                  type="reset"
                  className={getCategoryNavClasses(allCategory)}
                  onClick={() => handleMediaCategoryClick(allCategory)}
                >
                  All
                </Button>
              </li>
              {getCategories().map((category, index) => {
                const key = `category_${index}`;

                return (
                  <li key={key}>
                    <Button
                      type="reset"
                      className={getCategoryNavClasses(category)}
                      onClick={() => handleMediaCategoryClick(category)}
                    >
                      {category.name}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </nav>
          <div className="press__media-content" id="press__media-content">
            {results.length ? (
              <ul className="u-list">
                {results.map(({ node }) => (
                  <MediaItem
                    key={node.fileAbsolutePath}
                    title={node.frontmatter.title}
                    date={node.frontmatter.date}
                    link={node.frontmatter.link}
                    logo={node.frontmatter.logo}
                  />
                ))}
              </ul>
            ) : (
              <p className="u-textMuted">No media items found.</p>
            )}
          </div>
        </div>
      </div>
    </PressLayout>
  );
};

List.propTypes = {
  data: PropTypes.object,
  location: PropTypes.object.isRequired,
};

export default List;

export const pageQuery = graphql`
  query MediaList {
    list: allMarkdownRemark(
      sort: { fields: [frontmatter___date], order: DESC }
      filter: {
        fileAbsolutePath: { regex: "/src/media/" }
        frontmatter: { title: { ne: "BLUEPRINT" } }
      }
    ) {
      edges {
        node {
          ...MediaFragment
        }
      }
    }

    categories: allMarkdownRemark(
      filter: {
        fileAbsolutePath: { regex: "/src/media/" }
        frontmatter: { title: { ne: "BLUEPRINT" } }
      }
    ) {
      edges {
        node {
          frontmatter {
            categories
          }
        }
      }
    }
  }
`;
